import { DFValues } from '@vmware-react/vrlcm';
import {
  AddProductBinary,
  BinaryFileType,
  BinaryMap,
  ExecuteKbDTO,
  ExtendedStorageDetails,
  GenerateLogBundle,
  LogInSightAgentConfiguration,
  MyVmwareAccount,
  MyVMwareAccountSettings,
  MyVMwareProduct,
  NetworkDetails,
  OvaBundleFile,
  PatchMetadata,
  PatchOva,
  Policy,
  ProductBinary,
  Proxy,
  RequestId,
  RootPasswordChange,
  SnapShot,
  SSHDetails,
  SystemDetails,
  SystemUpgradeDetails,
  SystemUpgradeHistory,
  TelemetryInfo,
  UpgradeProgressStatus,
  VcfModeSettings
} from 'vrlcm-gui-types';
import { Http, isString } from 'vrlcm-gui-utils';
import { LcopsApiUri } from './uri';

const getProgressValue = (progress: string): number => {
  if (!progress || !isString(progress)) {
    return 0;
  }
  const progressValue = parseInt(progress.split('%')[0]);
  return isNaN(progressValue) ? 0 : progressValue;
};

const parseUpgradeStatus = async (APIResponse: UpgradeProgressStatus) => {
  const { state, version, latest_query_time, progress } = APIResponse;
  return {
    isUpgradeRunning: state === 'INSTALL_IN_PROGRESS',
    isUpToDate: state === 'UP_TO_DATE',
    isPrecheckInProgress: state === 'PRECHECK_IN_PROGRESS',
    isDiscoveryInProgress: state === 'DISCOVERY_IN_PROGRESS',
    installFailed: state === 'INSTALL_FAILED',
    version: version,
    timestamp: new Date(latest_query_time).getTime(),
    progress: getProgressValue(progress)
  };
};

export class SettingsApi {
  public static systemInformation: SystemDetails | null = null;

  public static async getSystemInformation() {
    if (SettingsApi.systemInformation) {
      return SettingsApi.systemInformation;
    }

    const response = await Http.get<SystemDetails>(
      LcopsApiUri.Settings.GET_SYSTEM_INFORMATION_URL
    );
    SettingsApi.systemInformation = response.data;
    return response.data;
  }
  public static async getVcfModeSettings() {
    const response = await Http.get<VcfModeSettings>(
      LcopsApiUri.Settings.GET_VCF_MODE_SETTINGS_URL
    );
    return response.data;
  }

  public static async getTelemetryInformation() {
    const response = await Http.get<TelemetryInfo>(
      LcopsApiUri.Settings.LCM_TELEMETRY_URL
    );
    return response.data;
  }

  public static async getMyVMwareAccountSettings() {
    const response = await Http.get<MyVMwareAccountSettings[]>(
      LcopsApiUri.Settings.MY_VMWARE_ACCOUNT_URL
    );
    return response.data.length > 0 ? response.data[0] : undefined;
  }

  public static importPatchOva = (
    ovaBundleFile: OvaBundleFile,
    fileType: BinaryFileType
  ): Promise<RequestId> => {
    const url =
      fileType === BinaryFileType.PATCH
        ? LcopsApiUri.Settings.UPLOAD_PATCH_URL
        : LcopsApiUri.Settings.IMPORT_PATCH_OVA_URL;
    return Http.post(url, ovaBundleFile).then(res => res.data);
  };
  public static getAvailableOvaFiles = (
    dirLocation: DFValues,
    fileType: BinaryFileType
  ): Promise<OvaBundleFile[]> => {
    const url =
      fileType === BinaryFileType.OVA_BUNDLE
        ? LcopsApiUri.Settings.GET_AVAILABLE_OVA_BUNDLE_FILES_URL
        : LcopsApiUri.Settings.GET_AVAILABLE_PATCH_FILES_URL;
    return Http.patch(url, dirLocation).then(res => res.data);
  };

  public static deleteMyVmwareAccount = (
    myVmwareAccount: MyVmwareAccount
  ): Promise<null> => {
    return Http.delete(LcopsApiUri.Settings.DELETE_MY_VMWARE_ACCOUNT_URL, {
      data: myVmwareAccount
    }).then(res => res.data);
  };
  public static getProductBinaries = (): Promise<ProductBinary[]> => {
    return Http.get(LcopsApiUri.Settings.GET_PRODUCT_BINARIES_URL).then(
      res => res.data
    );
  };

  public static deleteProductBinaries = (
    productBinary: ProductBinary
  ): Promise<RequestId> => {
    return Http.post(
      LcopsApiUri.Settings.DELETE_PRODUCT_BINARY_URL,
      productBinary
    ).then(res => res.data);
  };

  public static getCurrentPolicy = (): Promise<Policy[]> => {
    return Http.get(LcopsApiUri.Settings.GET_CURRENT_POLICY_URL).then(
      res => res.data
    );
  };
  public static getAvailablePolicies = (): Promise<Policy[]> => {
    return Http.get(LcopsApiUri.Settings.GET_AVAILABLE_POLICIES_URL).then(
      res => res.data
    );
  };

  public static getSystemUpgradeHistory = (): Promise<
    SystemUpgradeHistory[]
  > => {
    return Http.get(LcopsApiUri.Settings.LCM_UPGRADE_HISTORY).then(
      res => res.data
    );
  };
  public static getCurrentPatchDetails = (): Promise<any> => {
    return Http.get(LcopsApiUri.Settings.LCM_SELF_PATCH_HISTORY).then(
      res => res
    );
  };
  public static addProductBinary = (
    addProductBinary: AddProductBinary
  ): Promise<BinaryMap[]> => {
    return Http.patch(
      LcopsApiUri.Settings.DISCOVER_BINARIES_URL,
      addProductBinary
    ).then(res => res.data);
  };
  public static addProductBinaryMapping = (
    binaryMaps: BinaryMap[]
  ): Promise<RequestId> => {
    return Http.post(
      LcopsApiUri.Settings.DISCOVER_BINARIES_URL,
      binaryMaps
    ).then(res => res.data);
  };
  public static myVmwareProductDownloadAndMapping = (
    myVMwareProducts: MyVMwareProduct[]
  ): Promise<RequestId> => {
    return Http.post(
      LcopsApiUri.Settings.MYVMWARE_PRODUCTS_DOWNLOAD,
      myVMwareProducts
    ).then(res => res.data);
  };

  public static UpdateProxy = (proxy: Proxy): Promise<Proxy> => {
    return Http.post(LcopsApiUri.Settings.PROXY_URL, proxy).then(
      res => res.data
    );
  };
  public static GetProxy = (): Promise<Proxy> => {
    return Http.get(LcopsApiUri.Settings.PROXY_URL).then(res => res.data);
  };
  public static GetMyVMwareProductList = (): Promise<MyVMwareProduct[]> => {
    return Http.get(LcopsApiUri.Settings.GET_MYVMWARE_PRODUCTS_LIST).then(
      res => res.data
    );
  };

  public static changeRootPassword = (
    rootPassword: RootPasswordChange
  ): Promise<RequestId> => {
    return Http.post(
      LcopsApiUri.Settings.UPDATE_ROOT_PASSWORD_URL,
      rootPassword
    ).then(res => res.data);
  };
  public static extendStorage = (
    extendedStorageDetails: ExtendedStorageDetails
  ): Promise<RequestId> => {
    return Http.post(
      LcopsApiUri.Settings.EXTENDED_STORAGE_URL,
      extendedStorageDetails
    ).then(res => res.data);
  };
  public static lcmSnapshot = (snapShot: SnapShot): Promise<RequestId> => {
    return Http.post(LcopsApiUri.Settings.SNAPSHOT_URL, snapShot).then(
      res => res.data
    );
  };
  public static rebootSystem = (): Promise<RequestId> => {
    return Http.post(LcopsApiUri.Settings.REBOOT_SYSTEM_URL, {}).then(
      res => res.data
    );
  };
  public static generateLogBundle = (): Promise<RequestId> => {
    return Http.post(LcopsApiUri.Settings.GENERATE_LOG_BUNDLE_URL, {}).then(
      res => res.data
    );
  };
  public static getGenerateLogBundle = (): Promise<GenerateLogBundle> => {
    return Http.get(LcopsApiUri.Settings.GENERATE_LOG_BUNDLE_URL).then(
      res => res.data
    );
  };
  public static onCheckUpgradeAvailability = (
    systemUpgradeDetails: SystemUpgradeDetails
  ): Promise<{ version: string; buildNumber: string }> => {
    const body = {
      repositoryType: systemUpgradeDetails.repositoryType,
      repositoryUrl: systemUpgradeDetails.repositoryUrl,
      repositoryUserName: systemUpgradeDetails.repositoryUserName,
      repositoryPassword: systemUpgradeDetails.repositoryPassword,
      actionType: 'check'
    };
    return Http.post(
      LcopsApiUri.SettingsV2.CHECK_FOR_UPGRADE_AVAILABILITY_URL,
      body
    ).then(res => {
      const { build_number, version } = res.data;
      return {
        version: version,
        buildNumber: build_number,
        timestamp: new Date().getTime()
      };
    });
  };
  public static onUpgrade = (
    systemUpgradeDetails: SystemUpgradeDetails,
    version: string
  ) => {
    const body = {
      repositoryType: systemUpgradeDetails.repositoryType,
      repositoryUrl: systemUpgradeDetails.repositoryUrl,
      repositoryUserName: systemUpgradeDetails.repositoryUserName,
      repositoryPassword: systemUpgradeDetails.repositoryPassword,
      actionType: 'upgrade'
    };
    const url = LcopsApiUri.SettingsV2.TRIGGER_UPGRADE_URL;
    const searchParams = new URLSearchParams();
    searchParams.append('version', version);
    return Http.post(`${url}?${searchParams.toString()}`, body).then(
      res => res.data
    );
  };

  public static onCheckUpgradeStatus = (): Promise<{
    isUpgradeRunning: boolean;
    isUpToDate: boolean;
    isPrecheckInProgress: boolean;
    isDiscoveryInProgress: boolean;
    installFailed: boolean;
    version: string;
    timestamp: number;
    progress: number;
  }> => {
    return Http.get(LcopsApiUri.SettingsV2.TRIGGER_UPGRADE_URL).then(res =>
      parseUpgradeStatus(res.data)
    );
  };
  public static getAvailablePatches = (): Promise<PatchMetadata[]> => {
    return Http.get(LcopsApiUri.Settings.GET_AVAILABLE_PATCHES_URL).then(
      res => res.data
    );
  };

  public static getAvailablePatchOVAs = (): Promise<PatchOva[]> => {
    return Http.get(LcopsApiUri.Settings.GET_AVAILABLE_PATCH_OVAS_URL).then(
      res => res.data
    );
  };

  public static uploadPatch = (
    file: File,
    fileType: string
  ): Promise<RequestId> => {
    const formData = new FormData();
    let url;
    if (fileType === '.patch') {
      url = LcopsApiUri.Settings.UPLOAD_PATCH_URL;
    } else if (fileType === '.ovabundle') {
      url = LcopsApiUri.Settings.UPLOAD_PRODUCT_PATCH_OVA_URL;
    } else {
      url = LcopsApiUri.Settings.UPLOAD_PSPACK_URL;
    }
    formData.append('file', file);

    return Http.post(url, formData).then(res => res.data);
  };
  public static getNetworkInformation = (): Promise<NetworkDetails> => {
    return Http.get(LcopsApiUri.Settings.GET_NETWORK_INFORMATION_URL).then(
      res => res.data
    );
  };
  public static getSSHInformation = (): Promise<SSHDetails> => {
    return Http.get(LcopsApiUri.Settings.GET_SSH_INFORMATION_URL).then(
      res => res.data
    );
  };

  public static addOrUpdateMyVmwareAccount = (
    myVmwareAccount: MyVmwareAccount,
    editViewFlag: boolean
  ): Promise<Response> => {
    if (editViewFlag) {
      return Http.put(
        LcopsApiUri.Settings.UPDATE_MY_VMWARE_ACCOUNT_URL,
        myVmwareAccount
      ).then(res => res.data);
    } else {
      return Http.post(
        LcopsApiUri.Settings.MY_VMWARE_ACCOUNT_URL,
        myVmwareAccount
      ).then(res => res.data);
    }
  };

  public static validateMyVmwareAccount = async (
    myVmwareAccount: MyVmwareAccount
  ): Promise<{ requestId: string }> => {
    const response = await Http.patch(
      LcopsApiUri.Settings.MY_VMWARE_ACCOUNT_URL,
      myVmwareAccount
    );
    return response.data;
  };
  public static getMyVmwareAccounts = (): Promise<MyVmwareAccount[]> => {
    return Http.get(LcopsApiUri.Settings.MY_VMWARE_ACCOUNT_URL).then(
      res => res.data
    );
  };
  public static getAgentConfiguration = (): Promise<
    LogInSightAgentConfiguration
  > => {
    return Http.get(
      LcopsApiUri.Settings.GET_AGENT_CONFIGURATION_INFORMATION_URL
    ).then(res => res.data);
  };

  public static getAccountLockoutValues = async (key: string) => {
    const response = await Http.get(
      LcopsApiUri.ACCOUNT_LOCKOUT_CONFIG_PROPERTY_URL(key)
    );
    return response.data;
  };

  public static setAccountLockoutValues = async (
    key: string,
    value: string
  ) => {
    const response = await Http.put(
      LcopsApiUri.ACCOUNT_LOCKOUT_CONFIG_PROPERTY_URL(key),
      value,
      { headers: { 'Content-Type': 'text/plain' } }
    );
    return response.data;
  };

  public static listKbs = async (path: string) => {
    const url = LcopsApiUri.LIST_KBS_URL;
    const searchParams = new URLSearchParams();
    searchParams.append('path', path);
    const response = await Http.post(`${url}?${searchParams.toString()}`);
    return response.data;
  };

  public static validateKb = async (
    environmentIds: string[],
    kbScriptPath: string,
    productId: string,
    tenantId: string
  ) => {
    const url = LcopsApiUri.VALIDATE_KB;
    const searchParams = new URLSearchParams();
    searchParams.append('kbScriptPath', kbScriptPath);
    searchParams.append('productId', productId);
    searchParams.append('tenantId', tenantId);
    const body = {
      environmentIds: environmentIds
    };
    const response = await Http.post(`${url}?${searchParams.toString()}`, body);
    return response.data;
  };

  public static executeKb = async (executeKbDTO: ExecuteKbDTO) => {
    const url = LcopsApiUri.EXECUTE_KB;
    const response = await Http.post(url, executeKbDTO);
    return response.data;
  };
}
